import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store';
import { logout } from '../store/authSlice';
import ClickSparkWrapper from '../components/common/ClickSparkWrapper';
import Sidebar from '../components/common/Sidebar';
import AnalyticsStyleEmptyState from '../components/common/AnalyticsStyleEmptyState';
import TrendChart, { type ProgressChartPoint } from '../components/common/TrendChart';
import RankingRingChart from '../components/common/RankingRingChart';
import RadarChart from '../components/common/RadarChart';
import ActivityBar from '../components/common/ActivityGrid';
import SwotMiniChart from '../components/common/SwotMiniChart';
import './CandidateProfile.css';
import FeedbackDashboard from '../features/interview/components/FeedbackDashboard';
import { conversationHistoryToMessages } from '../features/interview/utils/conversationHistoryToMessages';
import CodingFeedbackDashboard from '../features/interview/components/CodingFeedbackDashboard';
import GdFeedbackDashboard from '../features/interview/components/GdFeedbackDashboard';
import type { CodingFeedbackReport } from '../features/interview/components/CodingFeedbackDashboard';
import type { GdSessionFeedbackResponse } from '../api/gdApi';
import { activityApi, ActivitySession } from '../api/activityApi';
import { getPeerComparison, type PeerComparisonResponse } from '../api/rankingApi';
import { shouldHideRankingBoard } from '../utils/rankingDisplay';
import { interviewApi } from '../api/interviewApi';
import { getOrgDepartmentLabels, onboardingApi } from '../api/onboardingApi';
import httpClient from '../api/httpClient';
import { roundsApi, UpcomingCustomInterview } from '../api/roundsApi';
import { vibeService } from '../services/VibeService';
import InterviewDetailsModal from '../components/interview/InterviewDetailsModal';
import AttendRoundModal from '../components/interview/AttendRoundModal';
import Toast from '../components/common/Toast';
import { ResumeEditorModal } from '../components/resume-editor';
import { parseMarkdownToLexical } from '../components/resume-editor/utils/generateResumeContent';
import { customiseResume, fetchCustomisedResume } from '../api/interviewBuilderApi';
import { useSyncCandidateNav } from '../contexts/CandidateNavContext';
import {
  DEFAULT_MOCK_INTERVIEW_TOKEN,
  MOCK_INTERVIEW_PUBLISHED_NOTICE,
} from '../features/interview/constants';
import { launchRoundSession } from '../utils/launchRoundSession';
import type { QuickLaunchRoundType } from '../utils/quickLaunchUtils';
import {
  DEFAULT_STUDENT_CODING_LANGUAGE,
  formatCodingLanguageLabel,
  isFreeChoiceCodingLanguage,
} from '../constants/codingLanguages';

// Types for AI Insights API
interface SubDimension {
  key: string;
  label: string;
  score: number | null;
  category: string | null;
  source?: string;
  points?: string[];
}

interface Dimension {
  key: string;
  label: string;
  score: number | null;
  category: string | null;
  sub_dimensions: SubDimension[];
}

interface Area {
  key: string;
  label: string;
  score: number | null;
  category: string | null;
  dimensions: Dimension[];
}

interface FocusRecommendation {
  skill: string;
  score: number;
  tip: string;
}

interface InsightsData {
  success: boolean;
  user_id: string;
  skill_scores: {
    communication: number;
    leadership: number;
    problem_solving: number;
    technical: number;
    creativity: number;
    adaptability: number;
  };
  overall_score: number;
  progress?: { week: string; overall: number }[];
  progress_chart?: {
    timezone: string;
    points: ProgressChartPoint[];
  } | null;
  session_counts: {
    Interview: number;
    GD: number;
    Coding: number;
  };
  sunburst_analysis: {
    areas: Area[];
  } | null;
  focus_recommendations: FocusRecommendation[];
  last_updated: string;
  error?: string | null;
}

/** User session scores: GET /api/orchestrator/analytics/session-scores/individual (user access token) */
interface SessionScoresSoftSkillsAgg {
  clarity?: number;
  confidence?: number;
  depth?: number;
  engagement?: number;
  examples?: number;
  relevance?: number;
}

interface SessionScoresAggregate {
  n_sessions: number;
  overall_score_mean?: number;
  soft_skills?: SessionScoresSoftSkillsAgg;
  programming_languages?: Record<string, number>;
}

interface SessionScoresIndividualResponse {
  user_id?: string;
  limit?: number;
  interview_types?: string[];
  aggregate: SessionScoresAggregate;
}

const SESSION_SCORES_SOFT_ROWS: { key: keyof SessionScoresSoftSkillsAgg; label: string }[] = [
  { key: 'clarity', label: 'Clarity' },
  { key: 'confidence', label: 'Confidence' },
  { key: 'depth', label: 'Depth' },
  { key: 'engagement', label: 'Engagement' },
  { key: 'examples', label: 'Examples' },
  { key: 'relevance', label: 'Relevance' },
];

function sessionScoresHasSkillsToShow(data: SessionScoresIndividualResponse | null): boolean {
  if (!data?.aggregate) return false;
  const langs = data.aggregate.programming_languages ?? {};
  if (Object.keys(langs).length > 0) return true;
  const soft = data.aggregate.soft_skills ?? {};
  return SESSION_SCORES_SOFT_ROWS.some(
    ({ key }) => typeof soft[key] === 'number' && !Number.isNaN(soft[key] as number)
  );
}

function formatProgrammingLanguageLabel(name: string): string {
  return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Programming scores are 0–100 in current API; values ≤1 are treated as legacy 0–1 fractions. */
function normalizeProgrammingLanguageScore(raw: number): number {
  if (!Number.isFinite(raw)) return 0;
  const scaled = raw > 1 ? raw : raw * 100;
  return Math.min(100, Math.max(0, scaled));
}

/** Soft scores are typically 0–10 (×10 for bar UI); values >10 are treated as already 0–100. */
function normalizeSessionSoftSkillScore(raw: number): number {
  if (!Number.isFinite(raw)) return 0;
  const scaled = raw > 10 ? raw : raw * 10;
  return Math.min(100, Math.max(0, scaled));
}

function truncateUpcomingLabel(s: string, maxLen: number): string {
  if (s.length <= maxLen) return s;
  return `${s.slice(0, maxLen)}…`;
}

function startOfLocalDay(d: Date = new Date()): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/** Start of the deadline's calendar day in local time (handles YYYY-MM-DD and ISO strings without UTC shifting "today"). */
function deadlineToLocalDayStart(deadline: string): Date {
  const head = deadline.trim().slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(head)) {
    const [y, mo, day] = head.split('-').map(Number);
    return new Date(y, mo - 1, day);
  }
  const dt = new Date(deadline);
  if (Number.isNaN(dt.getTime())) return new Date(0);
  return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
}

type UpcomingBadgeStatus = 'active' | 'completed' | 'expired';

function getUpcomingInterviewBadgeStatus(ci: UpcomingCustomInterview): UpcomingBadgeStatus {
  const isExpired = new Date(ci.deadline) < new Date();

  if (ci.rounds.length > 0) {
    const allCompleted = ci.rounds.every((r) => (r.status || 'pending') === 'completed');
    if (allCompleted) return 'completed';
    if (isExpired) return 'expired';
    return 'active';
  }

  return isExpired ? 'expired' : 'active';
}

interface CandidateData {
  name: string;
  email: string;
  position: string;
  experience: string;
  organizationName?: string;
  departmentName?: string;
  coreStrengths: string[];
  skills: {
    name: string;
    level: number;
    category: 'technical' | 'soft' | 'domain';
  }[];
  recentInterviews: {
    id: string;
    company: string;
    position: string;
    date: string;
    score: number;
    status: 'completed' | 'pending' | 'scheduled';
  }[];
  achievements: {
    title: string;
    description: string;
    date: string;
    type: 'certification' | 'project' | 'award';
  }[];
}


const CandidateProfile: React.FC = () => {
  const [candidateData, setCandidateData] = useState<CandidateData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeNav, setActiveNav] = useState<'overview' | 'skills' | 'activity' | 'interviews' | 'achievements' | 'settings'>('overview');
  useSyncCandidateNav(activeNav);
  const [activityData, setActivityData] = useState<ActivitySession[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState<string | null>(null);
  const [activityStatusFilter, setActivityStatusFilter] = useState<'all' | 'completed' | 'inuse' | 'abandoned'>('completed');
  const [activityTypeFilter, setActivityTypeFilter] = useState<'all' | 'Interview' | 'Coding' | 'GD'>('all');
  const [activitySortMode, setActivitySortMode] = useState<'recent' | 'score_desc' | 'score_asc'>('recent');
  const [activityFromDate, setActivityFromDate] = useState<string>('');
  const [activityToDate, setActivityToDate] = useState<string>('');
  const [activityTab, setActivityTab] = useState<'upcoming' | 'history'>('upcoming');
  const [upcomingInterviews, setUpcomingInterviews] = useState<UpcomingCustomInterview[]>([]);
  const [upcomingLoading, setUpcomingLoading] = useState(false);
  const [upcomingStatusFilter, setUpcomingStatusFilter] = useState<'all' | 'active' | 'completed' | 'expired'>('active');
  const [isUpcomingStatusMenuOpen, setIsUpcomingStatusMenuOpen] = useState(false);
  const [acceptingId, setAcceptingId] = useState<string | null>(null);
  const [infoModalInterviews, setInfoModalInterviews] = useState<UpcomingCustomInterview[] | null>(null);
  const [attendRoundTarget, setAttendRoundTarget] = useState<{ ciId: string; roundNum: number; type: string } | null>(null);
  const [attendSelectedLanguage, setAttendSelectedLanguage] = useState(DEFAULT_STUDENT_CODING_LANGUAGE);
  const [attendLoading, setAttendLoading] = useState(false);
  const [attendError, setAttendError] = useState('');
  const [isStatusMenuOpen, setIsStatusMenuOpen] = useState(false);
  const [isTypeMenuOpen, setIsTypeMenuOpen] = useState(false);
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const statusMenuRef = useRef<HTMLDivElement | null>(null);
  const typeMenuRef = useRef<HTMLDivElement | null>(null);
  const sortMenuRef = useRef<HTMLDivElement | null>(null);
  const datePickerRef = useRef<HTMLDivElement | null>(null);
  const upcomingStatusMenuRef = useRef<HTMLDivElement | null>(null);
  const activityHasFetchedRef = useRef(false);
  const historyStatusInitialAppliedRef = useRef(false);
  const historyStatusUserOverrideRef = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const activityTimelineRef = useRef<HTMLDivElement>(null);
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  const rootRef = useRef<HTMLDivElement>(null);
  const prevActiveNavRef = useRef<typeof activeNav | null>(null);
  const searchRef = useRef<HTMLTextAreaElement>(null);
  const [showResponse, setShowResponse] = useState(false);
  const [responseText, setResponseText] = useState<string>('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Feedback overlay state
  const [showFeedbackOverlay, setShowFeedbackOverlay] = useState(false);
  const [overlaySessionType, setOverlaySessionType] = useState<string | null>(null);
  const [overlayFeedbackData, setOverlayFeedbackData] = useState<{
    overall_score: number;
    readiness_level: string;
    performance_summary: string;
    strengths: string[];
    areas_for_improvement: string[];
    communication_skills: {
      clarity: number;
      confidence: number;
      engagement?: number;
      structure?: number;
      feedback?: string;
      clarity_feedback?: string;
      confidence_feedback?: string;
      engagement_feedback?: string;
    };
    professional_readiness?: {
      interview_skills: number;
      overall_impression: string;
    };
    content_quality?: {
      relevance: number;
      depth: number;
      examples: number;
      feedback: string;
    };
    interview_readiness?: {
      technical_readiness: string;
      behavioral_readiness: string;
      overall_confidence: string;
    };
    recommendations: string[];
    next_steps: string[];
  } | null>(null);
  const [overlayCodingFeedbackData, setOverlayCodingFeedbackData] = useState<CodingFeedbackReport | null>(null);
  const [overlayGdFeedbackData, setOverlayGdFeedbackData] = useState<GdSessionFeedbackResponse | null>(null);
  const [overlayMessages, setOverlayMessages] = useState<Array<{
    id: string;
    sender: 'ai' | 'user';
    text: string;
    timestamp: string;
  }>>([]);
  const [overlayLoading, setOverlayLoading] = useState(false);
  const [overlayError, setOverlayError] = useState<string | null>(null);

  // AI Insights state
  const [insightsData, setInsightsData] = useState<InsightsData | null>(null);
  const [insightsLoading, setInsightsLoading] = useState(false);

  // My Skills tab: session-scores analytics (orchestrator/analytics/session-scores/individual)
  const [sessionScoresData, setSessionScoresData] = useState<SessionScoresIndividualResponse | null>(null);
  const [sessionScoresLoading, setSessionScoresLoading] = useState(false);

  // Progress section: peer ranking dial vs improvement trend chart
  const [rankingData, setRankingData] = useState<PeerComparisonResponse | null>(null);
  const [rankingLoading, setRankingLoading] = useState(false);
  const [progressView, setProgressView] = useState<'ranking' | 'trend'>('ranking');

  useEffect(() => {
    if (isAuthenticated && user) {
      // Load candidate data
      loadCandidateData();
    }
  }, [isAuthenticated, user]);

  // Fetch AI Insights data
  useEffect(() => {
    const fetchInsights = async () => {
      if (!user?.id) return;
      
      try {
        setInsightsLoading(true);
        const response = await httpClient.get(`ai/insights/${user.id}`);
        
        if (response.data.success) {
          // Handle both response structures:
          // 1. Data nested under "insights": { success, user_id, insights: { skill_scores, ... } }
          // 2. Data at root level: { success, user_id, skill_scores, ... }
          const rawData = response.data;
          const insightsPayload = rawData.insights || rawData;
          
          const normalizedData: InsightsData = {
            success: rawData.success,
            user_id: rawData.user_id,
            skill_scores: insightsPayload.skill_scores,
            overall_score: insightsPayload.overall_score,
            progress: insightsPayload.progress,
            progress_chart: insightsPayload.progress_chart ?? null,
            session_counts: insightsPayload.session_counts,
            sunburst_analysis: insightsPayload.sunburst_analysis,
            focus_recommendations: insightsPayload.focus_recommendations,
            last_updated: insightsPayload.last_updated,
            error: insightsPayload.error,
          };
          
          setInsightsData(normalizedData);
        }
      } catch (err) {
        console.error('Failed to fetch insights:', err);
        // Silently fail - will show empty state
      } finally {
        setInsightsLoading(false);
      }
    };

    if (isAuthenticated && user?.id) {
      fetchInsights();
    }
  }, [isAuthenticated, user?.id]);

  // Fetch My Skills (session aggregate); user JWT is attached by httpClient.
  useEffect(() => {
    const fetchSessionScores = async () => {
      if (!user?.id) return;

      try {
        setSessionScoresLoading(true);
        const response = await httpClient.get<SessionScoresIndividualResponse>(
          'orchestrator/analytics/session-scores/individual',
          {
            params: {
              limit: 10,
              interview_types: 'coding,technical',
            },
          }
        );
        const body = response.data;
        if (body?.aggregate) {
          setSessionScoresData(body);
        } else {
          setSessionScoresData(null);
        }
      } catch (err) {
        console.error('Failed to fetch session scores (My Skills):', err);
        setSessionScoresData(null);
      } finally {
        setSessionScoresLoading(false);
      }
    };

    if (isAuthenticated && user?.id) {
      fetchSessionScores();
    }
  }, [isAuthenticated, user?.id]);

  // Fetch anonymous peer ranking for the Progress dial; user JWT is attached by httpClient.
  useEffect(() => {
    const fetchRanking = async () => {
      if (!user?.id) return;

      try {
        setRankingLoading(true);
        const body = await getPeerComparison();
        setRankingData(body ?? null);
      } catch (err) {
        console.error('Failed to fetch peer ranking:', err);
        setRankingData(null);
      } finally {
        setRankingLoading(false);
      }
    };

    if (isAuthenticated && user?.id) {
      fetchRanking();
    }
  }, [isAuthenticated, user?.id]);

  // Allow other pages to deep-link into a specific tab
  useEffect(() => {
    const navFromState = (location.state as { activeNav?: typeof activeNav } | null)?.activeNav;
    if (navFromState) {
      setActiveNav(navFromState);
      // If returning to activity tab, ensure data is present
      if (navFromState === 'activity') {
        fetchActivityData();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Fetch activity data on mount (needed for ActivityBar in dashboard)
  useEffect(() => {
    if (isAuthenticated && activityData.length === 0 && !activityLoading) {
      fetchActivityData();
    }
    if (isAuthenticated && upcomingInterviews.length === 0 && !upcomingLoading) {
      fetchUpcomingData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const prev = prevActiveNavRef.current;
    prevActiveNavRef.current = activeNav;
    if (prev === 'activity' && activeNav === 'overview') {
      fetchUpcomingData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeNav, isAuthenticated]);

  // Close user menu dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  // Determine if we should show the charts glass overlay (no data)
  const showChartsGlassOverlay = !insightsLoading && !insightsData?.skill_scores;
  const hideRankingBoard = shouldHideRankingBoard(rankingData);
  const showRankingView = !hideRankingBoard;

  const improvementChartPoints = useMemo((): ProgressChartPoint[] | null => {
    const fromChart = insightsData?.progress_chart?.points;
    if (fromChart && fromChart.length > 0) {
      return fromChart;
    }
    const legacy = insightsData?.progress;
    if (legacy && legacy.length > 0) {
      return legacy.map((p) => ({
        start: '',
        end: '',
        label: p.week,
        avg: typeof p.overall === 'number' ? p.overall : null,
        line_segment: 'previous',
      }));
    }
    return null;
  }, [insightsData?.progress_chart?.points, insightsData?.progress]);

  const upcomingInterviewsOnOrAfterToday = useMemo(() => {
    const todayStart = startOfLocalDay();
    return upcomingInterviews.filter(
      (ci) => deadlineToLocalDayStart(ci.deadline) >= todayStart
    );
  }, [upcomingInterviews]);

  const filteredUpcomingInterviews = useMemo(() => {
    if (upcomingStatusFilter === 'all') return upcomingInterviews;
    return upcomingInterviews.filter(
      (ci) => getUpcomingInterviewBadgeStatus(ci) === upcomingStatusFilter
    );
  }, [upcomingInterviews, upcomingStatusFilter]);

  /** Nearest deadline among interviews still valid (deadline ≥ start of today local). No fallback to past deadlines — those are expired. */
  const nearestUpcomingInterview = useMemo(() => {
    if (upcomingInterviewsOnOrAfterToday.length === 0) return null;
    return [...upcomingInterviewsOnOrAfterToday].sort(
      (a, b) =>
        deadlineToLocalDayStart(a.deadline).getTime() - deadlineToLocalDayStart(b.deadline).getTime()
    )[0];
  }, [upcomingInterviewsOnOrAfterToday]);

  const attendRoundConfig = useMemo(() => {
    if (!attendRoundTarget) {
      return undefined;
    }
    const interview = upcomingInterviews.find((ci) => ci.id === attendRoundTarget.ciId);
    return interview?.rounds.find((round) => round.round_number === attendRoundTarget.roundNum)?.config;
  }, [attendRoundTarget, upcomingInterviews]);

  const attendShowLanguagePicker =
    attendRoundTarget?.type === 'coding' &&
    isFreeChoiceCodingLanguage(attendRoundConfig?.language);

  useEffect(() => {
    if (attendRoundTarget) {
      setAttendSelectedLanguage(DEFAULT_STUDENT_CODING_LANGUAGE);
    }
  }, [attendRoundTarget]);

  const loadCandidateData = async () => {
    try {
      setLoading(true);
      vibeService.trigger('loading');

      // Use first_name/last_name from user profile (student table) when available
      let fullName: string | null = null;
      let organizationName: string | undefined;
      let departmentName: string | undefined;
      try {
        const profileResponse = await onboardingApi.getUserProfile();
        const { first_name, last_name } = profileResponse.data;
        if (first_name && last_name) {
          fullName = `${first_name} ${last_name}`.trim();
        } else if (first_name || last_name) {
          fullName = (first_name || last_name || '').trim();
        }
        const orgDept = getOrgDepartmentLabels(profileResponse.data);
        organizationName = orgDept.organizationName;
        departmentName = orgDept.departmentName;
      } catch {
        // Fallback if profile fetch fails
      }

      const mockData: CandidateData = {
        name: fullName || user?.email || 'Candidate',
        email: user?.email || "candidate@luci.ai",
        position: "Senior Software Engineer",
        experience: "5+ years",
        organizationName,
        departmentName,
        coreStrengths: [
          "Full-Stack Development",
          "System Architecture",
          "Team Leadership",
          "Problem Solving",
          "Communication"
        ],
        skills: [
          { name: "React", level: 9, category: 'technical' },
          { name: "Node.js", level: 8, category: 'technical' },
          { name: "TypeScript", level: 8, category: 'technical' },
          { name: "Python", level: 7, category: 'technical' },
          { name: "AWS", level: 7, category: 'technical' },
          { name: "Leadership", level: 8, category: 'soft' },
          { name: "Communication", level: 9, category: 'soft' },
          { name: "Problem Solving", level: 9, category: 'soft' },
          { name: "Agile/Scrum", level: 8, category: 'domain' },
          { name: "DevOps", level: 6, category: 'domain' }
        ],
        recentInterviews: [
          {
            id: '1',
            company: 'TechCorp Inc.',
            position: 'Senior Frontend Developer',
            date: '2024-01-15',
            score: 85,
            status: 'completed'
          },
          {
            id: '2',
            company: 'StartupXYZ',
            position: 'Full Stack Engineer',
            date: '2024-01-10',
            score: 78,
            status: 'completed'
          }
        ],
        achievements: [
          {
            title: "AWS Certified Solutions Architect",
            description: "Professional level certification in AWS cloud architecture",
            date: "2023-11-15",
            type: 'certification'
          },
          {
            title: "E-commerce Platform Redesign",
            description: "Led the complete redesign of a high-traffic e-commerce platform, improving performance by 40%",
            date: "2023-09-30",
            type: 'project'
          },
          {
            title: "Employee of the Quarter",
            description: "Recognized for outstanding performance and team collaboration",
            date: "2023-06-30",
            type: 'award'
          }
        ]
      };

      setCandidateData(mockData);
    } catch (error) {
      console.error('Error loading candidate data:', error);
      vibeService.trigger('error');
    } finally {
      setLoading(false);
      vibeService.dismiss();
    }
  };

  const fetchActivityData = async () => {
    if (!isAuthenticated) return;
    try {
      setActivityLoading(true);
      setActivityError(null);
      const response = await activityApi.getActivity();
      if (response.success && Array.isArray(response.activity)) {
        // Sort by updated_at descending (most recent first)
        const sortedActivity = [...response.activity].sort((a, b) => {
          const dateA = new Date(a.updated_at || a.created_at);
          const dateB = new Date(b.updated_at || b.created_at);
          return dateB.getTime() - dateA.getTime();
        });
        setActivityData(sortedActivity);
        vibeService.trigger('keep_pushing');
      } else {
        setActivityData([]);
      }
    } catch (error: any) {
      console.error('Failed to fetch activity data', error);
      setActivityError(error.message || 'Unable to load activity history');
      setActivityData([]);
      vibeService.trigger('error');
    } finally {
      activityHasFetchedRef.current = true;
      setActivityLoading(false);
    }
  };

  const fetchUpcomingData = async () => {
    if (!isAuthenticated) return;
    try {
      setUpcomingLoading(true);
      const { data } = await roundsApi.fetchUpcoming();
      if (data.success) {
        setUpcomingInterviews(data.interviews || []);
      }
    } catch (error) {
      console.error('Failed to fetch upcoming interviews', error);
      setUpcomingInterviews([]);
    } finally {
      setUpcomingLoading(false);
    }
  };

  const handleAcceptInterviews = async (ids: string[]) => {
    const unaccepted = ids.filter(id =>
      upcomingInterviews.find(ci => ci.id === id && !ci.accepted)
    );
    if (unaccepted.length === 0) return;
    try {
      setAcceptingId(unaccepted[0]);
      for (const id of unaccepted) {
        try {
          const { data } = await roundsApi.accept(id);
          if (data.success) {
            setUpcomingInterviews(prev =>
              prev.map(ci => ci.id === id ? { ...ci, accepted: true } : ci)
            );
            vibeService.trigger('success');
          }
        } catch (err) {
          console.error(`Failed to accept interview ${id}`, err);
          vibeService.trigger('error');
        }
      }
    } finally {
      setAcceptingId(null);
    }
  };

  const handleAttendRound = async (language?: string) => {
    if (!attendRoundTarget) return;
    setAttendLoading(true);
    setAttendError('');
    try {
      const result = await launchRoundSession(
        {
          interviewId: attendRoundTarget.ciId,
          roundNum: attendRoundTarget.roundNum,
          roundType: attendRoundTarget.type as QuickLaunchRoundType,
          ...(language ? { language } : {}),
        },
        navigate
      );
      if (result.error) {
        setAttendError(result.error);
        return;
      }
      setAttendRoundTarget(null);
    } finally {
      setAttendLoading(false);
    }
  };

  // Handle click on activity bar dot - navigate to activity tab and scroll to date
  const handleActivityDateClick = useCallback((date: Date) => {
    // Format date as YYYY-MM-DD for the id
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateKey = `${year}-${month}-${day}`;
    
    // Set the query param and switch to activity tab
    setSearchParams({ highlightDate: dateKey });
    setActiveNav('activity');
  }, [setSearchParams]);

  const handleUpcomingPillClick = useCallback(
    (id: string) => {
      setSearchParams({ highlightUpcoming: id });
      setActiveNav('activity');
      setActivityTab('upcoming');
    },
    [setSearchParams]
  );

  // Scroll to highlighted date when query param is set and activity tab is active
  useEffect(() => {
    const highlightDate = searchParams.get('highlightDate');
    if (highlightDate && activeNav === 'activity' && !activityLoading && activityData.length > 0) {
      // Small delay to ensure DOM is rendered
      const timeoutId = setTimeout(() => {
        const element = document.getElementById(`activity-date-${highlightDate}`);
        if (element) {
          const scrollContainer = element.closest('.profile-main-content');
          if (scrollContainer) {
            const elementRect = element.getBoundingClientRect();
            const containerRect = scrollContainer.getBoundingClientRect();
            const offsetTop = elementRect.top - containerRect.top + scrollContainer.scrollTop;
            // Scroll with 24px padding from top for better visibility
            scrollContainer.scrollTo({
              top: offsetTop - 24,
              behavior: 'smooth'
            });
          } else {
            // Fallback: use scrollIntoView with start alignment
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
        // Clear the param after scrolling
        setSearchParams({});
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [searchParams, activeNav, activityLoading, activityData.length, setSearchParams]);

  // Scroll to specific session when scrollToSession param is set
  useEffect(() => {
    const scrollToSession = searchParams.get('scrollToSession');
    if (scrollToSession && activeNav === 'activity' && activityTab === 'history' && !activityLoading && activityData.length > 0) {
      const timeoutId = setTimeout(() => {
        const element = document.getElementById(`activity-session-${scrollToSession}`);
        if (element) {
          const scrollContainer = element.closest('.profile-main-content');
          if (scrollContainer) {
            const elementRect = element.getBoundingClientRect();
            const containerRect = scrollContainer.getBoundingClientRect();
            const offsetTop = elementRect.top - containerRect.top + scrollContainer.scrollTop;
            scrollContainer.scrollTo({
              top: offsetTop - 24,
              behavior: 'smooth'
            });
            // Highlight the session briefly
            element.classList.add('highlight-session');
            setTimeout(() => element.classList.remove('highlight-session'), 2000);
          } else {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
        setSearchParams({});
      }, 150);
      return () => clearTimeout(timeoutId);
    }
  }, [searchParams, activeNav, activityTab, activityLoading, activityData.length, setSearchParams]);

  // Scroll to highlighted upcoming interview card
  useEffect(() => {
    const highlightUpcoming = searchParams.get('highlightUpcoming');
    if (
      highlightUpcoming &&
      activeNav === 'activity' &&
      activityTab === 'upcoming' &&
      !upcomingLoading
    ) {
      const timeoutId = setTimeout(() => {
        const element = document.getElementById(`upcoming-interview-${highlightUpcoming}`);
        if (element) {
          const scrollContainer = element.closest('.profile-main-content');
          if (scrollContainer) {
            const elementRect = element.getBoundingClientRect();
            const containerRect = scrollContainer.getBoundingClientRect();
            const offsetTop = elementRect.top - containerRect.top + scrollContainer.scrollTop;
            scrollContainer.scrollTo({
              top: offsetTop - 24,
              behavior: 'smooth',
            });
          } else {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
          element.classList.add('highlight-upcoming');
          setTimeout(() => element.classList.remove('highlight-upcoming'), 2000);
        }
        setSearchParams({});
      }, 150);
      return () => clearTimeout(timeoutId);
    }
  }, [searchParams, activeNav, activityTab, upcomingLoading, setSearchParams]);

  // Navigate to session in activity history
  const navigateToSessionInHistory = useCallback((sessionId: string) => {
    setSearchParams({ scrollToSession: sessionId });
    setActiveNav('activity');
    setActivityTab('history');
  }, [setSearchParams]);

  // Fetch individual session feedback for overlay (via orchestrator — dual-source)
  const openFeedbackOverlay = async (sessionId: string) => {
    try {
      setOverlayLoading(true);
      setOverlayError(null);
      setShowFeedbackOverlay(true);

      const data = await interviewApi.getSessionFeedback(sessionId);
      
      // Handle pending state - feedback still being generated
      if (data.status === 'pending') {
        setOverlayError(data.message || 'Feedback is being generated. Please try again in a few seconds.');
        setOverlayLoading(false);
        return;
      }
      
      if (!data.success || !data.session) {
        throw new Error('Invalid session feedback response');
      }

      const session = data.session;
      const sessionType = data.session_type || session.session_type || 'Interview';
      setOverlaySessionType(sessionType);

      if (sessionType === 'GD') {
        if (!data.evaluation) {
          setOverlayError('Feedback is not available yet. Please try again in a few seconds.');
          setOverlayLoading(false);
          return;
        }
        // For GD sessions, pass the full response to GdFeedbackDashboard
        setOverlayGdFeedbackData(data as GdSessionFeedbackResponse);
        setOverlayFeedbackData(null);
        setOverlayCodingFeedbackData(null);
        setOverlayMessages([]);
        return;
      }

      if (sessionType === 'Coding') {
        const evaluation = data.evaluation || {};
        const feedback = evaluation.feedback || {};
        const authenticity = evaluation.authenticity_assessment || {};

        setOverlayCodingFeedbackData({
          session: {
            session_id: session.session_id,
            session_date: session.session_date || session.interview_date,
            problem_id: session.problem_id,
            language: session.language,
            mode: session.mode,
            final_status: session.final_status,
            final_score: session.final_score,
            attempt_count: session.attempt_count,
          },
          evaluation: {
            overall_score: evaluation.overall_score ?? session.final_score ?? 0,
            overall_summary: evaluation.overall_summary || '',
            dimensions: evaluation.dimensions || {},
            feedback: {
              summary: feedback.summary || '',
              strengths: feedback.strengths || [],
              improvements: feedback.improvements || [],
              critical_issues: feedback.critical_issues || [],
            },
            authenticity_assessment: {
              confidence: authenticity.confidence,
              signals: authenticity.signals || [],
              concerns: authenticity.concerns || [],
            },
          },
        });
        setOverlayFeedbackData(null);
        setOverlayGdFeedbackData(null);
        setOverlayMessages([]);
        return;
      }

      const df = session.detailed_feedback || {};

      setOverlayFeedbackData({
        overall_score: df.overall_score ?? 0,
        readiness_level: df.readiness_level || 'unknown',
        performance_summary: df.performance_summary || '',
        strengths: df.strengths || [],
        areas_for_improvement: df.areas_for_improvement || [],
        communication_skills:
          df.communication_skills || {
            clarity: 0,
            confidence: 0,
            feedback: 'No communication feedback available.',
          },
        professional_readiness: df.professional_readiness,
        content_quality: df.content_quality,
        interview_readiness: df.interview_readiness,
        recommendations: df.recommendations || [],
        next_steps: df.next_steps || [],
      });

      setOverlayMessages(
        conversationHistoryToMessages(
          session.conversation_history,
          session.interview_date || new Date().toISOString()
        )
      );
      setOverlayCodingFeedbackData(null);
    } catch (err: any) {
      console.error('Failed to load session feedback for overlay', err);
      setOverlayError(err.message || 'Unable to load detailed feedback');
    } finally {
      setOverlayLoading(false);
    }
  };

  const closeFeedbackOverlay = () => {
    setShowFeedbackOverlay(false);
    setOverlayFeedbackData(null);
    setOverlayCodingFeedbackData(null);
    setOverlayGdFeedbackData(null);
    setOverlayMessages([]);
    setOverlayError(null);
    setOverlaySessionType(null);
  };

  const hasCompletedActivity = useMemo(
    () => activityData.some((session) => session.status === 'completed'),
    [activityData],
  );

  const historyStatusDefault: typeof activityStatusFilter = hasCompletedActivity ? 'completed' : 'all';

  // Default to completed after activity data loads; fall back to all if none completed
  useEffect(() => {
    if (
      activityLoading ||
      !activityHasFetchedRef.current ||
      historyStatusInitialAppliedRef.current ||
      historyStatusUserOverrideRef.current
    ) {
      return;
    }
    historyStatusInitialAppliedRef.current = true;
    setActivityStatusFilter(hasCompletedActivity ? 'completed' : 'all');
  }, [activityLoading, hasCompletedActivity]);

  const setHistoryStatusFilter = useCallback((filter: typeof activityStatusFilter) => {
    historyStatusUserOverrideRef.current = true;
    setActivityStatusFilter(filter);
  }, []);

  // Filter and sort activity data
  const filteredActivityData = useMemo(() => {
    // Exclude pending (created) sessions — show completed, in-progress, and abandoned
    let filtered = activityData.filter(session => session.status === 'completed' || session.status === 'inuse' || session.status === 'abandoned');
    
    // Filter by status
    if (activityStatusFilter !== 'all') {
      filtered = filtered.filter(session => session.status === activityStatusFilter);
    }
    
    // Filter by type
    if (activityTypeFilter !== 'all') {
      filtered = filtered.filter(session => session.session_type === activityTypeFilter);
    }
    
    // Filter by date range
    if (activityFromDate) {
      const fromDate = new Date(`${activityFromDate}T00:00:00`);
      filtered = filtered.filter(session => {
        const sessionDate = new Date(session.updated_at || session.created_at);
        return sessionDate >= fromDate;
      });
    }
    if (activityToDate) {
      const toDate = new Date(`${activityToDate}T23:59:59.999`);
      filtered = filtered.filter(session => {
        const sessionDate = new Date(session.updated_at || session.created_at);
        return sessionDate <= toDate;
      });
    }
    
    // Sort
    filtered.sort((a, b) => {
      if (activitySortMode === 'score_desc') {
        const scoreA = a.feedback_summary?.overall_score ?? -1;
        const scoreB = b.feedback_summary?.overall_score ?? -1;
        return scoreB - scoreA;
      }
      if (activitySortMode === 'score_asc') {
        const scoreA = a.feedback_summary?.overall_score ?? Infinity;
        const scoreB = b.feedback_summary?.overall_score ?? Infinity;
        return scoreA - scoreB;
      }
      // Default: recent (most recent first)
      const dateA = new Date(a.updated_at || a.created_at);
      const dateB = new Date(b.updated_at || b.created_at);
      return dateB.getTime() - dateA.getTime();
    });
    
    return filtered;
  }, [activityData, activityStatusFilter, activityTypeFilter, activitySortMode, activityFromDate, activityToDate]);

  const isActivityDateFilterActive = Boolean(activityFromDate || activityToDate);

  // Close menus on outside click
  useEffect(() => {
    if (!isStatusMenuOpen && !isTypeMenuOpen && !isSortMenuOpen && !isDatePickerOpen && !isUpcomingStatusMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (isStatusMenuOpen && statusMenuRef.current && !statusMenuRef.current.contains(e.target as Node)) {
        setIsStatusMenuOpen(false);
      }
      if (isTypeMenuOpen && typeMenuRef.current && !typeMenuRef.current.contains(e.target as Node)) {
        setIsTypeMenuOpen(false);
      }
      if (isSortMenuOpen && sortMenuRef.current && !sortMenuRef.current.contains(e.target as Node)) {
        setIsSortMenuOpen(false);
      }
      if (isDatePickerOpen && datePickerRef.current && !datePickerRef.current.contains(e.target as Node)) {
        setIsDatePickerOpen(false);
      }
      if (isUpcomingStatusMenuOpen && upcomingStatusMenuRef.current && !upcomingStatusMenuRef.current.contains(e.target as Node)) {
        setIsUpcomingStatusMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isStatusMenuOpen, isTypeMenuOpen, isSortMenuOpen, isDatePickerOpen, isUpcomingStatusMenuOpen]);

  const handleLogout = async () => {
    try {
      await dispatch(logout()).unwrap();
      // Navigation will be handled by the ProtectedRoute component
    } catch (error) {
      console.error('Logout failed:', error);
      // Even if logout fails on server, the Redux state will be cleared
    }
  };

  const handleStartInterview = () => {
    navigate('/interview');
  };

  const getSkillColor = (level: number): string => {
    if (level >= 8) return '#45d291'; // Green (success)
    if (level >= 6) return '#4ea8ff'; // Blue (accent)
    if (level >= 4) return '#F59E0B'; // Orange
    return '#EF4444'; // Red
  };

  // Get skill color for 0-100 scale (used for insights data)
  const getInsightSkillColor = (score: number): string => {
    if (score >= 85) return '#00FF88'; // Green - Strength
    if (score >= 70) return '#7FFF00'; // Lime - Improvements
    if (score >= 50) return '#BA68C8'; // Purple - Weakness
    return '#008B8B'; // Teal - Critical
  };

  // Get category label for 0-100 scale
  const getCategoryLabel = (score: number): string => {
    if (score >= 85) return 'Strength';
    if (score >= 70) return 'Improvements';
    if (score >= 50) return 'Weakness';
    return 'Critical';
  };

  const getStatusBadgeClass = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'completed';
      case 'inuse':
        return 'in-progress';
      case 'scheduled':
        return 'scheduled';
      case 'abandoned':
      case 'cancelled':
      case 'canceled':
        return 'abandoned';
      default:
        return 'project';
    }
  };

  const getAchievementIcon = (type: string): string => {
    switch (type) {
      case 'certification': return '🏆';
      case 'project': return '🚀';
      case 'award': return '⭐';
      default: return '📋';
    }
  };

  // Attach cursor-following glow to interactive items within this page only
  useEffect(() => {
    const container = rootRef.current;
    if (!container) return;
    const selectors = [
      '.stat-card',
      '.chart-placeholder',
      '.ai-insights-box',
      '.insight-item-compact',
      '.activity-item',
      '.achievement-item',
      '.skill-item',
      '.interview-card',
      '.achievement-card',
      '.sidebar-action-btn',
      '.nav-item',
      '.notification-button',
      '.logout-button'
    ].join(',');

    const elements = Array.from(container.querySelectorAll<HTMLElement>(selectors));
    elements.forEach((el) => el.classList.add('glowable'));

    const onMove = (e: MouseEvent) => {
      const el = e.currentTarget as HTMLElement;
      const rect = el.getBoundingClientRect();
      el.style.setProperty('--mx', `${e.clientX - rect.left}px`);
      el.style.setProperty('--my', `${e.clientY - rect.top}px`);
    };
    const onLeave = (e: MouseEvent) => {
      const el = e.currentTarget as HTMLElement;
      el.style.removeProperty('--mx');
      el.style.removeProperty('--my');
    };

    elements.forEach((el) => {
      el.addEventListener('mousemove', onMove as EventListener);
      el.addEventListener('mouseleave', onLeave as EventListener);
    });

    return () => {
      elements.forEach((el) => {
        el.removeEventListener('mousemove', onMove as EventListener);
        el.removeEventListener('mouseleave', onLeave as EventListener);
      });
    };
  }, [activeNav, loading]);

  // Auto-grow search textarea height as text wraps
  const autosizeSearch = () => {
    const el = searchRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const computed = window.getComputedStyle(el);
    const lineHeight = parseFloat(computed.lineHeight || '18');
    const paddingTop = parseFloat(computed.paddingTop || '0');
    const paddingBottom = parseFloat(computed.paddingBottom || '0');
    const maxLines = 5;
    const limit = Math.round(lineHeight * maxLines + paddingTop + paddingBottom);
    const minHeight = 30; // keep in sync with --search-min-h in CSS
    const next = Math.max(minHeight, Math.min(el.scrollHeight, limit));
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > limit ? 'auto' : 'hidden';
    const wrap = el.parentElement as HTMLElement | null;
    if (wrap) {
      wrap.style.height = `${next}px`; // ensure wrapper (glow) grows with textarea
    }
  };

  useEffect(() => {
    autosizeSearch();
  }, []);

  const handleSearchSubmit = () => {
    const q = (searchRef.current?.value || '').trim();
    if (!q) return;
    // TODO: wire to real search/submit action
    // For now, log and clear
    // eslint-disable-next-line no-console
    console.log('Search submit:', q);
    setResponseText('This will be the AI search box. Interact with your account with chats');
    setShowResponse(true);
    if (searchRef.current) {
      searchRef.current.value = '';
      autosizeSearch();
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSearchSubmit();
    }
  };

  const initials = candidateData?.name.split(' ').map(n => n[0]).join('').toUpperCase() || 'GM';

  return (
    <>
    {loading ? (
      <div className="candidate-profile">
        <div className="profile-loading">
          <div className="loading-spinner"></div>
          <p>Loading your profile...</p>
        </div>
      </div>
    ) : !candidateData ? (
      <div className="candidate-profile">
        <div className="profile-error">
          <div className="error-icon">⚠️</div>
          <h3>Unable to Load Profile</h3>
          <p>There was an error loading your profile data.</p>
          <button onClick={() => loadCandidateData()} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    ) : (
    <div className="candidate-profile" ref={rootRef} style={{ display: 'flex' }}>
      {/* Fixed Sidebar */}
      <div style={{ position: 'fixed', left: 0, top: 0, height: '100vh', zIndex: 100 }}>
        <Sidebar
          activeNav={activeNav}
          onSelectNav={(key) => setActiveNav(key)}
          initials={initials}
          name={candidateData?.name || 'Candidate'}
          email={candidateData?.email}
          organizationName={candidateData?.organizationName}
          departmentName={candidateData?.departmentName}
          onStartInterview={handleStartInterview}
          onLogout={handleLogout}
          upcomingCount={upcomingInterviewsOnOrAfterToday.length}
        />
      </div>

      {/* Main Content Area - offset by sidebar width */}
      <div style={{ marginLeft: '260px', flex: 1, minHeight: '100vh', position: 'relative' }}>

      {/* Dashboard Content */}
      <div className="dashboard-wrapper scrolled">
        <div className="profile-content">
          {/* Main Content Area */}
          <div className="profile-main-content" style={{ flex: 1, width: '100%' }}>
            {activeNav === 'overview' && (
              <div className="overview-content">
                <div className="welcome-section">
                  <div className="welcome-left">
                    <h1>Welcome back, {candidateData.name.split(' ')[0]}!</h1>
                    <p className="welcome-message">Your profile is ready to go. Let's get started!</p>
                  </div>
                  <div className="welcome-right">
                    {upcomingLoading && upcomingInterviews.length === 0 && (
                      <span className="welcome-upcoming-loading">Loading upcoming…</span>
                    )}
                    {nearestUpcomingInterview && (
                      <button
                        type="button"
                        className={`badge-pill badge-pill--upcoming ${
                          nearestUpcomingInterview.accepted
                            ? 'badge-pill--upcoming-accepted'
                            : 'badge-pill--upcoming-pending'
                        }`}
                        onClick={() => handleUpcomingPillClick(nearestUpcomingInterview.id)}
                        aria-label={`Open Activity — upcoming: ${nearestUpcomingInterview.name}`}
                      >
                        <svg
                          className="badge-pill__notify-icon"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden
                        >
                          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                        </svg>
                        <span className="badge-pill__upcoming-text">
                          <span className="badge-pill__upcoming-name">
                            {truncateUpcomingLabel(nearestUpcomingInterview.name, 20)}
                          </span>
                          <span className="badge-pill__upcoming-sep"> · </span>
                          <span className="badge-pill__upcoming-date">
                            {new Date(nearestUpcomingInterview.deadline).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </span>
                        </span>
                      </button>
                    )}
                  </div>
                </div>

                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-label">Average Score</div>
                    <div className="stat-number">{insightsData?.overall_score ?? '-'}</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-label">Completed Sessions</div>
                    <div className="stat-number">
                      {insightsData?.session_counts 
                        ? (insightsData.session_counts.Interview || 0) + (insightsData.session_counts.GD || 0) + (insightsData.session_counts.Coding || 0)
                        : '-'}
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-label">Focus Areas</div>
                    <div className="stat-number">{insightsData?.focus_recommendations?.length ?? '-'}</div>
                  </div>
                </div>

                <div className="progress-section">
                  <div className="section-header">
                    <h3>Progress</h3>
                    <span className="section-subtitle">Last 30 days</span>
                  </div>
                  <ActivityBar sessions={activityData} onDateClick={handleActivityDateClick} />
                  <div className={`charts-wrapper ${showChartsGlassOverlay ? 'charts-blurred' : ''}`}>
                    <div className="charts-container">
                      <div className="chart-cell">
                        <div className="chart-cell-header">
                          <h4 className="chart-cell-title">
                            {progressView === 'ranking' && showRankingView
                              ? rankingData?.cohort_size != null
                                ? `Performance among ${rankingData.cohort_size} peers`
                                : 'Performance among peers'
                              : 'Overall improvement'}
                          </h4>
                          {showRankingView && (
                          <div className="progress-view-toggle" role="tablist" aria-label="Progress view">
                            <button
                              type="button"
                              role="tab"
                              aria-selected={progressView === 'ranking'}
                              className={`progress-view-toggle-btn ${progressView === 'ranking' ? 'active' : ''}`}
                              onClick={() => setProgressView('ranking')}
                            >
                              Ranking
                            </button>
                            <button
                              type="button"
                              role="tab"
                              aria-selected={progressView === 'trend'}
                              className={`progress-view-toggle-btn ${progressView === 'trend' ? 'active' : ''}`}
                              onClick={() => setProgressView('trend')}
                            >
                              Trend
                            </button>
                          </div>
                          )}
                        </div>
                        {progressView === 'ranking' && showRankingView ? (
                          <RankingRingChart data={rankingData} />
                        ) : (
                          <TrendChart points={improvementChartPoints ?? undefined} />
                        )}
                      </div>
                      <div className="chart-cell">
                        <h4 className="chart-cell-title">Skill score</h4>
                        <RadarChart 
                          labels={insightsData?.skill_scores ? [
                            'Communication',
                            'Leadership',
                            'Problem Solving',
                            'Technical',
                            'Creativity',
                            'Adaptability'
                          ] : undefined}
                          values={insightsData?.skill_scores ? [
                            insightsData.skill_scores.communication,
                            insightsData.skill_scores.leadership,
                            insightsData.skill_scores.problem_solving,
                            insightsData.skill_scores.technical,
                            insightsData.skill_scores.creativity,
                            insightsData.skill_scores.adaptability
                          ] : undefined}
                        />
                      </div>
                    </div>
                    {insightsLoading && (
                      <div className="charts-loader-overlay">
                        <div className="loading-spinner"></div>
                        <span>Loading insights...</span>
                      </div>
                    )}
                    {showChartsGlassOverlay && (
                      <div className="charts-glass-overlay">
                        <span className="charts-glass-title">Progress unlocking soon</span>
                        <span className="charts-glass-subtext">attempt a few interviews to view progress</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* AI Insights Section - Minimal Design */}
                <div className="ai-insights-minimal">
                  <div className="section-header">
                    <h3>AI Insights</h3>
                    <button className="see-all-link" onClick={() => navigate('/analytics')}>View Analysis</button>
                  </div>
                  <div className="insights-minimal-container">
                    {insightsLoading ? (
                      <div className="insights-mini-loading">
                        <div className="mini-loading-spinner"></div>
                        <span>Loading insights...</span>
                      </div>
                    ) : insightsData?.sunburst_analysis?.areas && insightsData.sunburst_analysis.areas.length > 0 ? (
                      <>
                        <div className="insights-minimal-left">
                          <SwotMiniChart 
                            areas={insightsData.sunburst_analysis.areas} 
                            size={220}
                            onClick={() => navigate('/analytics')}
                          />
                        </div>
                        <div className="insights-minimal-divider" />
                        <div className="insights-minimal-right">
                          <div className="focus-minimal-header">
                            <span className="focus-minimal-title">Focus Areas</span>
                          </div>
                          <div className="focus-minimal-list">
                            {insightsData.focus_recommendations && insightsData.focus_recommendations.length > 0 ? (
                              insightsData.focus_recommendations.map((rec, index) => (
                                <div key={index} className="focus-minimal-item">
                                  <div className="focus-minimal-item-left">
                                    <span className="focus-minimal-rank">{index + 1}</span>
                                    <div className="focus-minimal-info">
                                      <span className="focus-minimal-skill">{rec.skill}</span>
                                      <span className="focus-minimal-tip">{rec.tip}</span>
                                    </div>
                                  </div>
                                  <span className="focus-minimal-score">{rec.score}</span>
                                </div>
                              ))
                            ) : (
                              <div className="focus-minimal-empty">No recommendations yet</div>
                            )}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="insights-mini-empty">
                        <div className="mini-empty-icon">
                          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                        </div>
                        <div className="mini-empty-text">
                          <span className="mini-empty-title">No insights yet</span>
                          <span className="mini-empty-desc">Complete sessions to see your SWOT analysis</span>
                        </div>
                        <button className="mini-empty-cta" onClick={handleStartInterview}>
                          Start a Session
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="recent-activity-section">
                  <div className="section-header">
                    <h3>Recent Activity</h3>
                    <button className="see-all-link" onClick={() => setActiveNav('activity')}>See all</button>
                  </div>
                  <div className="activity-list dashboard-activity-list">
                    {activityData
                      .filter(session => session.status === 'completed' || session.status === 'inuse' || session.status === 'abandoned')
                      .sort((a, b) => new Date(b.updated_at || b.created_at).getTime() - new Date(a.updated_at || a.created_at).getTime())
                      .slice(0, 3)
                      .map((session) => {
                        const sessionDate = new Date(session.updated_at || session.created_at);
                        const dateStr = sessionDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                        const timeStr = sessionDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                        const isCompleted = session.status === 'completed';
                        const isInProgress = session.status === 'inuse';
                        const statusLabel = isCompleted ? 'Completed' : isInProgress ? 'In Progress' : 'Abandoned';
                        const statusClass = isCompleted ? 'completed' : isInProgress ? 'in-progress' : 'abandoned';
                        
                        // Build title based on session type
                        let title = '';
                        if (session.session_type === 'Interview') {
                          const details = session.interview_details;
                          if (details?.difficulty || details?.experience_focus || details?.mode) {
                            const parts = [
                              details.difficulty,
                              details.experience_focus,
                              details.mode
                            ].filter(Boolean);
                            title = `Interview | ${parts.join(' | ')}`;
                          } else {
                            title = `Interview | ${session.interview_details?.interview_type || 'General'}`;
                          }
                        } else if (session.session_type === 'GD') {
                          title = `Group Discussion | ${session.gd_details?.topic || 'Topic'}`;
                        } else if (session.session_type === 'Coding') {
                          const codingDetails = session.coding_details;
                          const interviewDetails = session.interview_details as { challenge_name?: string; language?: string; difficulty?: string } | undefined;
                          const challengeName = codingDetails?.challenge_name || interviewDetails?.challenge_name;
                          const language = codingDetails?.language || interviewDetails?.language;
                          const difficulty = codingDetails?.difficulty || interviewDetails?.difficulty;
                          const parts = [
                            challengeName,
                            language ? language.charAt(0).toUpperCase() + language.slice(1) : null,
                            difficulty ? difficulty.charAt(0).toUpperCase() + difficulty.slice(1) : null,
                          ].filter(Boolean);
                          title = parts.length > 0 ? `Coding | ${parts.join(' | ')}` : 'Coding | Challenge';
                        }
                        
                        const shapeClass = session.session_type === 'Interview' 
                          ? 'triangle' 
                          : session.session_type === 'Coding' 
                            ? 'square' 
                            : 'diamond';

                        return (
                          <div key={session.session_id} className="dashboard-activity-item">
                            <div className="dashboard-activity-left">
                              <span className={`activity-type-icon ${shapeClass}`}></span>
                              <span className="dashboard-activity-title">{title}</span>
                            </div>
                            <div className="dashboard-activity-right">
                              <span className="dashboard-activity-datetime">{dateStr} · {timeStr}</span>
                              <span className={`activity-status-badge ${statusClass}`}>
                                {statusLabel}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    {activityData.filter(s => s.status === 'completed' || s.status === 'inuse' || s.status === 'abandoned').length === 0 && (
                      <div className="dashboard-activity-empty">No recent activity</div>
                    )}
                  </div>
                </div>

              </div>
            )}

            {activeNav === 'skills' && (
              <div className="skills-content">
                <div className="welcome-section">
                  <div className="welcome-left">
                    <h1>My Skills</h1>
                    <p className="welcome-message">Your skill assessment based on completed sessions</p>
                  </div>
                  <div className="welcome-right">
                    {sessionScoresData?.aggregate && (
                      <span className="skills-updated">
                        Based on {sessionScoresData.aggregate.n_sessions} sessions
                      </span>
                    )}
                  </div>
                </div>

                {sessionScoresLoading ? (
                  <div className="skills-loading">
                    <div className="loading-spinner"></div>
                    <span>Loading skills data...</span>
                  </div>
                ) : (
                  <>
                    {/* Technical Skills — aggregate.programming_languages (0–100, or legacy 0–1) */}
                    {sessionScoresData?.aggregate &&
                      Object.keys(sessionScoresData.aggregate.programming_languages ?? {}).length > 0 && (
                      <div className="skills-section">
                        <h3 className="skills-section-title">Technical Skills</h3>
                        <p className="skills-section-subtitle">Languages from your coding and technical sessions</p>
                        <div className="skill-scores-grid">
                          {Object.entries(sessionScoresData.aggregate.programming_languages ?? {})
                            .sort(([a], [b]) => a.localeCompare(b))
                            .map(([lang, rawScore]) => {
                              const displayScore = normalizeProgrammingLanguageScore(rawScore);
                              return (
                                <div key={lang} className="skill-score-card">
                                  <div className="skill-score-header">
                                    <span className="skill-score-name">{formatProgrammingLanguageLabel(lang)}</span>
                                    <span
                                      className="skill-score-value"
                                      style={{ color: getInsightSkillColor(displayScore) }}
                                    >
                                      {Math.round(displayScore)}
                                    </span>
                                  </div>
                                  <div className="skill-score-bar">
                                    <div
                                      className="skill-score-bar-fill"
                                      style={{
                                        width: `${displayScore}%`,
                                        backgroundColor: getInsightSkillColor(displayScore),
                                      }}
                                    />
                                  </div>
                                  <div className="skill-score-meta">
                                    <span
                                      className="skill-score-category"
                                      style={{ color: getInsightSkillColor(displayScore) }}
                                    >
                                      {getCategoryLabel(displayScore)}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    )}

                    {/* Soft Skills — aggregate.soft_skills (0–10 ×10, or already 0–100 if values > 10) */}
                    {sessionScoresData?.aggregate &&
                      SESSION_SCORES_SOFT_ROWS.some(({ key }) => {
                        const v = sessionScoresData.aggregate.soft_skills?.[key];
                        return typeof v === 'number' && !Number.isNaN(v);
                      }) && (
                      <div className="skills-section">
                        <h3 className="skills-section-title">Soft Skills</h3>
                        <p className="skills-section-subtitle">Communication and content quality (from session feedback)</p>
                        <div className="skill-scores-grid">
                          {SESSION_SCORES_SOFT_ROWS.filter(({ key }) => {
                            const v = sessionScoresData.aggregate.soft_skills?.[key];
                            return typeof v === 'number' && !Number.isNaN(v);
                          }).map(({ key, label }) => {
                            const raw = sessionScoresData.aggregate.soft_skills?.[key] as number;
                            const displayScore = normalizeSessionSoftSkillScore(raw);
                            return (
                              <div key={key} className="skill-score-card">
                                <div className="skill-score-header">
                                  <span className="skill-score-name">{label}</span>
                                  <span
                                    className="skill-score-value"
                                    style={{ color: getInsightSkillColor(displayScore) }}
                                  >
                                    {Math.round(displayScore)}
                                  </span>
                                </div>
                                <div className="skill-score-bar">
                                  <div
                                    className="skill-score-bar-fill"
                                    style={{
                                      width: `${displayScore}%`,
                                      backgroundColor: getInsightSkillColor(displayScore),
                                    }}
                                  />
                                </div>
                                <div className="skill-score-category" style={{ color: getInsightSkillColor(displayScore) }}>
                                  {getCategoryLabel(displayScore)}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {!sessionScoresHasSkillsToShow(sessionScoresData) && (
                      <div className="skills-empty">
                        <div className="empty-icon">
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                        </div>
                        <p className="empty-title">No skills data yet</p>
                        <p className="empty-message">Complete some interviews or assessments to see your skill scores</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {activeNav === 'interviews' && (
              <div className="interviews-content">
                <h3>Interview History</h3>
                <div className="interviews-list">
                  {candidateData.recentInterviews.map((interview) => (
                    <div key={interview.id} className="interview-card">
                      <div className="interview-header">
                        <div className="interview-info">
                          <h4>{interview.position}</h4>
                          <p>{interview.company}</p>
                        </div>
                        <div className="interview-status">
                          <span className={`type-badge ${getStatusBadgeClass(interview.status)}`}>
                            {interview.status}
                          </span>
                        </div>
                      </div>
                      <div className="interview-details">
                        <div className="interview-date">
                          📅 {new Date(interview.date).toLocaleDateString()}
                        </div>
                        {interview.status === 'completed' && (
                          <div className="interview-score">
                            📊 Score: {interview.score}/100
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}


            {activeNav === 'activity' && (
              <div className="my-activity-content">
                <div className="welcome-section">
                  <div className="welcome-left">
                    <h1>My Activity</h1>
                    <p className="welcome-message">Timeline of interviews and assessments taken</p>
                  </div>
                </div>

                <div className="activity-tabs">
                  <div className="activity-tabs-list">
                    <button
                      className={`activity-tab-btn ${activityTab === 'upcoming' ? 'active' : ''}`}
                      onClick={() => setActivityTab('upcoming')}
                    >
                      Upcoming interviews
                    </button>
                    <button
                      className={`activity-tab-btn ${activityTab === 'history' ? 'active' : ''}`}
                      onClick={() => setActivityTab('history')}
                    >
                      Interview history
                    </button>
                  </div>
                  <div className="activity-tabs-refresh">
                    <span className="activity-tabs-refresh-hint">
                      Refresh page to reactivate in-progress rounds
                    </span>
                    <button
                      type="button"
                      className="activity-tabs-refresh-btn"
                      onClick={fetchUpcomingData}
                      disabled={upcomingLoading}
                      aria-label="Refresh upcoming interviews"
                      title="Refresh upcoming interviews"
                    >
                      <svg
                        className={upcomingLoading ? 'spinning' : ''}
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M21 2v6h-6" />
                        <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
                        <path d="M3 22v-6h6" />
                        <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
                      </svg>
                    </button>
                  </div>
                </div>

                {activityTab === 'history' && (
                <>
                <div className="activity-filters">
                  <div className="activity-filters-left">
                    <div className="activity-filter-summary">
                      {activityStatusFilter === 'all' 
                        ? `Showing all ${filteredActivityData.length} activities`
                        : activityStatusFilter === 'completed'
                          ? `Showing ${filteredActivityData.length} completed`
                          : activityStatusFilter === 'inuse'
                            ? `Showing ${filteredActivityData.length} in progress`
                            : `Showing ${filteredActivityData.length} abandoned`}
                    </div>
                  </div>
                  <div className="activity-filters-right">
                    {/* Sort Filter */}
                    <div className="activity-sort-wrap" ref={sortMenuRef}>
                      <button
                        className={`activity-filter-btn ${activitySortMode !== 'recent' ? 'active' : ''} ${isSortMenuOpen ? 'open' : ''}`}
                        onClick={() => { setIsSortMenuOpen(v => !v); setIsStatusMenuOpen(false); setIsTypeMenuOpen(false); setIsDatePickerOpen(false); }}
                        aria-haspopup="menu"
                        aria-expanded={isSortMenuOpen}
                      >
                        {activitySortMode === 'score_desc'
                          ? 'Score ↓'
                          : activitySortMode === 'score_asc'
                            ? 'Score ↑'
                            : 'Recent'}
                      </button>

                      {isSortMenuOpen && (
                        <div className="user-dropdown-menu activity-sort-menu" role="menu">
                          <button
                            className="user-dropdown-item"
                            onClick={() => { setActivitySortMode('recent'); setIsSortMenuOpen(false); }}
                            role="menuitem"
                          >
                            Recent
                          </button>
                          <button
                            className="user-dropdown-item"
                            onClick={() => { 
                              setActivitySortMode('score_desc'); 
                              setActivityStatusFilter('completed'); 
                              setIsSortMenuOpen(false); 
                            }}
                            role="menuitem"
                          >
                            Score (High to Low)
                          </button>
                          <button
                            className="user-dropdown-item"
                            onClick={() => { 
                              setActivitySortMode('score_asc'); 
                              setActivityStatusFilter('completed'); 
                              setIsSortMenuOpen(false); 
                            }}
                            role="menuitem"
                          >
                            Score (Low to High)
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Status Filter */}
                    <div className="activity-status-filter-wrap" ref={statusMenuRef}>
                      <button
                        className={`activity-filter-btn ${activityStatusFilter !== historyStatusDefault ? 'active' : ''} ${isStatusMenuOpen ? 'open' : ''}`}
                        onClick={() => { setIsStatusMenuOpen(v => !v); setIsSortMenuOpen(false); setIsTypeMenuOpen(false); setIsDatePickerOpen(false); }}
                        aria-haspopup="menu"
                        aria-expanded={isStatusMenuOpen}
                      >
                        {activityStatusFilter === 'all' 
                          ? 'All Status' 
                          : activityStatusFilter === 'completed' 
                            ? 'Completed' 
                            : activityStatusFilter === 'inuse'
                              ? 'In Progress'
                              : 'Abandoned'}
                      </button>

                      {isStatusMenuOpen && (
                        <div className="user-dropdown-menu activity-status-menu" role="menu">
                          <button
                            className="user-dropdown-item"
                            onClick={() => { setHistoryStatusFilter('all'); setIsStatusMenuOpen(false); }}
                            role="menuitem"
                          >
                            All Status
                          </button>
                          <button
                            className="user-dropdown-item"
                            onClick={() => { setHistoryStatusFilter('completed'); setIsStatusMenuOpen(false); }}
                            role="menuitem"
                          >
                            Completed
                          </button>
                          <button
                            className="user-dropdown-item"
                            onClick={() => { setHistoryStatusFilter('inuse'); setIsStatusMenuOpen(false); }}
                            role="menuitem"
                          >
                            In Progress
                          </button>
                          <button
                            className="user-dropdown-item"
                            onClick={() => { setHistoryStatusFilter('abandoned'); setIsStatusMenuOpen(false); }}
                            role="menuitem"
                          >
                            Abandoned
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Type Filter */}
                    <div className="activity-type-filter-wrap" ref={typeMenuRef}>
                      <button
                        className={`activity-filter-btn ${activityTypeFilter !== 'all' ? 'active' : ''} ${isTypeMenuOpen ? 'open' : ''}`}
                        onClick={() => { setIsTypeMenuOpen(v => !v); setIsStatusMenuOpen(false); setIsSortMenuOpen(false); setIsDatePickerOpen(false); }}
                        aria-haspopup="menu"
                        aria-expanded={isTypeMenuOpen}
                      >
                        {activityTypeFilter === 'all' 
                          ? 'All Types' 
                          : activityTypeFilter === 'Interview' 
                            ? <><span className="activity-type-icon triangle inline"></span> Interview</>
                            : activityTypeFilter === 'Coding'
                              ? <><span className="activity-type-icon square inline"></span> Coding</>
                              : <><span className="activity-type-icon diamond inline"></span> GD</>}
                      </button>

                      {isTypeMenuOpen && (
                        <div className="user-dropdown-menu activity-type-menu" role="menu">
                          <button
                            className="user-dropdown-item"
                            onClick={() => { setActivityTypeFilter('all'); setIsTypeMenuOpen(false); }}
                            role="menuitem"
                          >
                            All Types
                          </button>
                          <button
                            className="user-dropdown-item"
                            onClick={() => { setActivityTypeFilter('Interview'); setIsTypeMenuOpen(false); }}
                            role="menuitem"
                          >
                            <span className="activity-type-icon triangle inline"></span> Interview
                          </button>
                          <button
                            className="user-dropdown-item"
                            onClick={() => { setActivityTypeFilter('Coding'); setIsTypeMenuOpen(false); }}
                            role="menuitem"
                          >
                            <span className="activity-type-icon square inline"></span> Coding
                          </button>
                          <button
                            className="user-dropdown-item"
                            onClick={() => { setActivityTypeFilter('GD'); setIsTypeMenuOpen(false); }}
                            role="menuitem"
                          >
                            <span className="activity-type-icon diamond inline"></span> GD
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Date Filter */}
                    <div className="activity-date-filter-wrap" ref={datePickerRef}>
                      <button
                        className={`activity-filter-btn ${isActivityDateFilterActive ? 'active' : ''} ${isDatePickerOpen ? 'open' : ''}`}
                        onClick={() => { setIsDatePickerOpen(v => !v); setIsStatusMenuOpen(false); setIsTypeMenuOpen(false); setIsSortMenuOpen(false); }}
                      >
                        {isActivityDateFilterActive 
                          ? (activityFromDate && activityToDate 
                              ? `${new Date(activityFromDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(activityToDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                              : activityFromDate 
                                ? `From ${new Date(activityFromDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                                : `To ${new Date(activityToDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`)
                          : 'Date'}
                      </button>

                      {isDatePickerOpen && (
                        <div className="activity-date-popover">
                          <div className="activity-date-inputs">
                            <div className="activity-date-input-group">
                              <label>From</label>
                              <input
                                type="date"
                                value={activityFromDate}
                                onChange={(e) => setActivityFromDate(e.target.value)}
                                className="feedback-filter-date"
                              />
                            </div>
                            <div className="activity-date-input-group">
                              <label>To</label>
                              <input
                                type="date"
                                value={activityToDate}
                                onChange={(e) => setActivityToDate(e.target.value)}
                                className="feedback-filter-date"
                              />
                            </div>
                          </div>
                          <div className="activity-date-actions">
                            <button
                              className="activity-date-apply"
                              onClick={() => setIsDatePickerOpen(false)}
                            >
                              Apply
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Clear Filters */}
                    {(activityStatusFilter !== historyStatusDefault || activityTypeFilter !== 'all' || isActivityDateFilterActive || activitySortMode !== 'recent') && (
                      <button
                        className="activity-filter-clear"
                        onClick={() => {
                          historyStatusUserOverrideRef.current = false;
                          setActivityStatusFilter(historyStatusDefault);
                          setActivityTypeFilter('all');
                          setActivitySortMode('recent');
                          setActivityFromDate('');
                          setActivityToDate('');
                        }}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>

                {activityLoading && (
                  <div className="activity-loading-card">
                    <div className="loading-spinner"></div>
                    <span>Loading your activity history...</span>
                  </div>
                )}

                {activityError && !activityLoading && (
                  <div className="activity-error-card">
                    <span>{activityError}</span>
                    <button onClick={fetchActivityData}>Retry</button>
                  </div>
                )}

                {!activityLoading && !activityError && activityData.length === 0 && (
                  <AnalyticsStyleEmptyState
                    title="No activity yet."
                    description="Start an interview or assessment to see your history here."
                    icon={
                      <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    }
                    onPrimary={handleStartInterview}
                    onSecondary={() => navigate('/candidate')}
                    hints={[
                      { icon: '📋', text: 'Complete a session to see it in your timeline' },
                      { icon: '📊', text: 'Scores and feedback appear here after each activity' },
                    ]}
                  />
                )}

                {!activityLoading && !activityError && activityData.length > 0 && filteredActivityData.length === 0 && (
                  <div className="activity-empty-state">
                    <h2 className="activity-empty-heading">
                      No {activityStatusFilter === 'completed' ? 'completed' : 'abandoned'} activities found.
                    </h2>
                  </div>
                )}

                {!activityLoading && !activityError && filteredActivityData.length > 0 && (
                  <div className="activity-timeline">
                    {/* Group activities by month */}
                    {Object.entries(
                      filteredActivityData.reduce((groups, session) => {
                        const date = new Date(session.updated_at || session.created_at);
                        const monthKey = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                        if (!groups[monthKey]) {
                          groups[monthKey] = [];
                        }
                        groups[monthKey].push(session);
                        return groups;
                      }, {} as Record<string, ActivitySession[]>)
                    ).map(([month, sessions]: [string, ActivitySession[]], monthIndex) => (
                      <div key={month} className="activity-month-group">
                    
                        <div className="activity-month-content">
                          {/* Group by date within month */}
                          {Object.entries(
                            sessions.reduce((dateGroups: Record<string, ActivitySession[]>, session: ActivitySession) => {
                              const date = new Date(session.updated_at || session.created_at);
                              const dateKey = date.toISOString().split('T')[0];
                              if (!dateGroups[dateKey]) {
                                dateGroups[dateKey] = [];
                              }
                              dateGroups[dateKey].push(session);
                              return dateGroups;
                            }, {} as Record<string, ActivitySession[]>)
                          ).map(([dateKey, dateSessions]: [string, ActivitySession[]], dateIndex) => {
                            const date = new Date(dateKey);
                            const isLatest = monthIndex === 0 && dateIndex === 0;
                            return (
                              <div key={dateKey} id={`activity-date-${dateKey}`} className="activity-date-row">
                                <div className="activity-date-column">
                                  <div className="activity-date-content">
                                    <div className="activity-date-text">
                                      {date.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
                                    </div>
                                    <span className="activity-timeline-dot"></span>
                                  </div>
                                </div>
                                <div className="activity-day-card">
                                  {dateSessions.map((session: ActivitySession, sessionIndex: number) => {
                                    const sessionTime = new Date(session.updated_at || session.created_at)
                                      .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                                    
                                    // Determine session title based on type
                                    let sessionTitle = '';
                                    if (session.session_type === 'Interview') {
                                      const details = session.interview_details;
                                      // Build interview details string from difficulty, experience_focus, mode
                                      const detailParts: string[] = [];
                                      if (details?.difficulty) {
                                        detailParts.push(details.difficulty.charAt(0).toUpperCase() + details.difficulty.slice(1));
                                      }
                                      if (details?.experience_focus) {
                                        detailParts.push(details.experience_focus.charAt(0).toUpperCase() + details.experience_focus.slice(1).replace('_', ' '));
                                      }
                                      if (details?.mode) {
                                        detailParts.push(details.mode.charAt(0).toUpperCase() + details.mode.slice(1));
                                      }
                                      const detailsStr = detailParts.length > 0 
                                        ? detailParts.join(' | ')
                                        : (details?.interview_type || 'General');
                                      sessionTitle = `Individual interview | ${detailsStr}`;
                                    } else if (session.session_type === 'GD') {
                                      const topic = session.gd_details?.topic || 'Topic';
                                      sessionTitle = `GD | ${topic}`;
                                    } else if (session.session_type === 'Coding') {
                                      // Check both coding_details and interview_details (backend may use either)
                                      const codingDetails = session.coding_details;
                                      const interviewDetails = session.interview_details as { challenge_name?: string; language?: string; difficulty?: string } | undefined;
                                      const challengeName = codingDetails?.challenge_name || interviewDetails?.challenge_name;
                                      const language = codingDetails?.language || interviewDetails?.language;
                                      const difficulty = codingDetails?.difficulty || interviewDetails?.difficulty;
                                      const codingParts = [
                                        challengeName,
                                        language ? language.charAt(0).toUpperCase() + language.slice(1) : null,
                                        difficulty ? difficulty.charAt(0).toUpperCase() + difficulty.slice(1) : null,
                                      ].filter(Boolean);
                                      sessionTitle = codingParts.length > 0 ? `Coding | ${codingParts.join(' | ')}` : 'Coding | Challenge';
                                    }

                                    // Determine status display
                                    const isCompleted = session.status === 'completed';
                                    const isInProgress = session.status === 'inuse';
                                    const isAbandoned = session.status === 'abandoned';
                                    const statusText = isCompleted ? 'Completed' : isInProgress ? 'In Progress' : isAbandoned ? 'Abandoned' : 'Pending';
                                    const statusClass = isCompleted ? 'completed' : isInProgress ? 'in-progress' : isAbandoned ? 'abandoned' : 'pending';

                                    // Score display
                                    const noScore = isAbandoned || isInProgress;
                                    const score = session.feedback_summary?.overall_score;
                                    const scoreDisplay = noScore ? 'N/A' : (score !== undefined && score !== null ? `${score}` : 'N/A');
                                    const isScoreNA = noScore || score === undefined || score === null;

                                    // Summary display
                                    const summary = session.feedback_summary?.performance_summary || 
                                      (isAbandoned ? 'No summary or feedback for abandoned sessions' : isInProgress ? 'Session still in progress' : 'Overall summary');

                                    const isLastItem = sessionIndex === dateSessions.length - 1;

                                    // Determine shape class based on session type
                                    const shapeClass = session.session_type === 'Interview' 
                                      ? 'triangle' 
                                      : session.session_type === 'Coding' 
                                        ? 'square' 
                                        : 'diamond';

                                    return (
                                      <div key={session.session_id} id={`activity-session-${session.session_id}`} className={`activity-item ${isLastItem ? 'last' : ''}`}>
                                        <div className="activity-item-header">
                                          <div className="activity-item-title">
                                            <span className={`activity-type-icon ${shapeClass}`}></span>
                                            {sessionTitle}
                                          </div>
                                          <div className="activity-item-meta">
                                            <span className="activity-item-time">{sessionTime}</span>
                                            {(isCompleted || isInProgress || isAbandoned) && (
                                              <span className={`activity-status-badge ${statusClass}`}>{statusText}</span>
                                            )}
                                          </div>
                                        </div>
                                        <div className="activity-item-body">
                                          <div className="activity-item-score">
                                            <span className="activity-score-label">Score</span>
                                            <span className="activity-score-separator">/</span>
                                            <span className={`activity-score-value ${isScoreNA ? 'na' : ''}`}>{scoreDisplay}</span>
                                          </div>
                                          <span className="activity-body-dot">·</span>
                                          <div className="activity-item-summary">{summary}</div>
                                        </div>
                                        {isCompleted && (
                                          <div className="activity-item-actions">
                                            <button
                                              className="activity-view-feedback"
                                              onClick={() => openFeedbackOverlay(session.session_id)}
                                            >
                                              View feedback
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                </>
                )}

                {activityTab === 'upcoming' && (
                  <>
                  <div className="activity-filters activity-filters--upcoming">
                    <div className="activity-filters-left">
                      <div className="activity-filter-summary">
                        {upcomingStatusFilter === 'all'
                          ? `Showing all ${filteredUpcomingInterviews.length} interviews`
                          : upcomingStatusFilter === 'active'
                            ? `Showing ${filteredUpcomingInterviews.length} active`
                            : upcomingStatusFilter === 'completed'
                              ? `Showing ${filteredUpcomingInterviews.length} completed`
                              : `Showing ${filteredUpcomingInterviews.length} expired`}
                      </div>
                    </div>
                    <div className="activity-filters-right">
                      <div className="activity-status-filter-wrap" ref={upcomingStatusMenuRef}>
                        <button
                          className={`activity-filter-btn ${upcomingStatusFilter !== 'active' ? 'active' : ''} ${isUpcomingStatusMenuOpen ? 'open' : ''}`}
                          onClick={() => setIsUpcomingStatusMenuOpen((v) => !v)}
                          aria-haspopup="menu"
                          aria-expanded={isUpcomingStatusMenuOpen}
                        >
                          {upcomingStatusFilter === 'all'
                            ? 'All'
                            : upcomingStatusFilter === 'completed'
                              ? 'Completed'
                              : upcomingStatusFilter === 'expired'
                                ? 'Expired'
                                : 'Active'}
                        </button>

                        {isUpcomingStatusMenuOpen && (
                          <div className="user-dropdown-menu activity-status-menu" role="menu">
                            <button
                              className="user-dropdown-item"
                              onClick={() => { setUpcomingStatusFilter('active'); setIsUpcomingStatusMenuOpen(false); }}
                              role="menuitem"
                            >
                              Active
                            </button>
                            <button
                              className="user-dropdown-item"
                              onClick={() => { setUpcomingStatusFilter('all'); setIsUpcomingStatusMenuOpen(false); }}
                              role="menuitem"
                            >
                              All
                            </button>
                            <button
                              className="user-dropdown-item"
                              onClick={() => { setUpcomingStatusFilter('completed'); setIsUpcomingStatusMenuOpen(false); }}
                              role="menuitem"
                            >
                              Completed
                            </button>
                            <button
                              className="user-dropdown-item"
                              onClick={() => { setUpcomingStatusFilter('expired'); setIsUpcomingStatusMenuOpen(false); }}
                              role="menuitem"
                            >
                              Expired
                            </button>
                          </div>
                        )}
                      </div>

                      {upcomingStatusFilter !== 'active' && (
                        <button
                          type="button"
                          className="activity-filter-clear"
                          onClick={() => setUpcomingStatusFilter('active')}
                        >
                          Reset filter
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="activity-timeline activity-timeline--upcoming">
                    {upcomingLoading && (
                      <div className="activity-loading-state">Loading upcoming interviews...</div>
                    )}

                    {!upcomingLoading && filteredUpcomingInterviews.length > 0 && (() => {
                      const sorted = [...filteredUpcomingInterviews].sort((a, b) =>
                        new Date(a.deadline).getTime() - new Date(b.deadline).getTime()
                      );

                      const grouped = sorted.reduce((acc, ci) => {
                        const key = ci.deadline;
                        if (!acc[key]) acc[key] = [];
                        acc[key].push(ci);
                        return acc;
                      }, {} as Record<string, typeof upcomingInterviews>);

                      const roundTypeLabel = (type: string) =>
                        type === 'aptitude' ? 'Mock Interview' : type === 'coding' ? 'Coding Assessment' : type === 'gd' ? 'Group Discussion' : type;

                      const roundShapeClass = (type: string) =>
                        type === 'aptitude' ? 'triangle' : type === 'coding' ? 'square' : 'diamond';

                      return Object.entries(grouped).map(([dateKey, interviews]) => {
                        const deadlineDate = new Date(dateKey);
                        const isExpired = deadlineDate < new Date();

                        return (
                          <div key={dateKey} className="activity-date-row">
                            <div className="activity-date-column">
                              <div className="activity-date-content">
                                <div className="activity-date-text">
                                  <span className="activity-deadline-label">To be taken by</span>
                                  {deadlineDate.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
                                </div>
                                <span className="activity-timeline-dot"></span>
                              </div>
                            </div>
                            <div className="activity-day-cards">
                              {interviews.map((ci) => {
                                const isAccepting = acceptingId === ci.id;
                                const isProcessed = ci.jdProcessingStatus === 'processed';
                                const actionableRound = ci.accepted
                                  ? ci.rounds.find((r, idx) => {
                                      const st = r.status || 'pending';
                                      if (st === 'abandoned') return true;
                                      if (st !== 'pending') return false;
                                      return ci.rounds.slice(0, idx).every(
                                        prev => prev.status === 'completed'
                                      );
                                    })
                                  : null;

                                return (
                                  <div
                                    key={ci.id}
                                    id={`upcoming-interview-${ci.id}`}
                                    className="activity-day-card"
                                  >
                                    <div className="activity-card-header">
                                      <div className="activity-card-header-left">
                                        <div className="activity-card-header-name">
                                          {ci.name}
                                        </div>
                                        <div className="activity-card-header-links">
                                          <span
                                            className="activity-view-info-link"
                                            onClick={() => setInfoModalInterviews([ci])}
                                          >
                                            View Interview Info
                                          </span>
                                        </div>
                                      </div>
                                      <div className="activity-card-header-actions">
                                        <span
                                          className="activity-view-info-link"
                                          onClick={() => navigate(`/build-resume?interviewId=${ci.id}`)}
                                        >
                                          Customise resume
                                        </span>
                                        {!ci.accepted ? (
                                          <button
                                            className="activity-accept-btn"
                                            disabled={isAccepting || !isProcessed}
                                            onClick={() => handleAcceptInterviews([ci.id])}
                                          >
                                            {isAccepting ? 'Accepting...' : !isProcessed ? 'Processing JD...' : 'Accept Interview'}
                                          </button>
                                        ) : (
                                          <button className="activity-accept-btn accepted" disabled>
                                            Accepted
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                    {ci.rounds.length > 0 ? (
                                      ci.rounds.map((round) => {
                                        const scheduledDate = round.scheduledDate
                                          ? new Date(round.scheduledDate).toLocaleDateString('en-US', { day: 'numeric', month: 'short' })
                                          : null;
                                        const roundStatus = round.status || 'pending';
                                        const isActionable = actionableRound && round.round_number === actionableRound.round_number && !isExpired;
                                        const isRetake = isActionable && roundStatus === 'abandoned';
                                        return (
                                          <div key={`${ci.id}-${round.round_number}`} className={`activity-item${isActionable ? ' next-round' : ''}`}>
                                            <div className="activity-item-header">
                                              <div className="activity-item-title">
                                                <span className={`activity-type-icon ${roundShapeClass(round.type)}`}></span>
                                                Round {round.round_number}: {roundTypeLabel(round.type)}
                                              </div>
                                              <div className="activity-item-meta">
                                                {isActionable && (
                                                  <button
                                                    className={`attend-round-btn${isRetake ? ' retake' : ''}`}
                                                    onClick={() => setAttendRoundTarget({ ciId: ci.id, roundNum: round.round_number, type: round.type })}
                                                  >
                                                    {isRetake ? 'Retake Round' : 'Attend Round'}
                                                  </button>
                                                )}
                                                {roundStatus === 'completed' && (() => {
                                                  const roundKey = String(round.round_number);
                                                  const roundData = ci.round_session?.session_interaction?.[roundKey] as { session_id?: string; overall_score?: number } | undefined;
                                                  const sessionId = round.session_id || roundData?.session_id;
                                                  const score = round.overall_score ?? roundData?.overall_score;
                                                  const hasScore = score !== undefined && score !== null;
                                                  return (
                                                    <>
                                                      {hasScore && (
                                                        <div className="round-score-display">
                                                          <span className="round-score-label">Score</span>
                                                          <span className="round-score-separator">/</span>
                                                          <span className="round-score-value">{score}</span>
                                                        </div>
                                                      )}
                                                      {sessionId && (
                                                        <span
                                                          className="view-feedback-link"
                                                          onClick={() => openFeedbackOverlay(sessionId)}
                                                        >
                                                          View Feedback
                                                        </span>
                                                      )}
                                                    </>
                                                  );
                                                })()}
                                                <span className={`activity-status-badge ${roundStatus === 'completed' ? 'completed' : roundStatus === 'inuse' ? 'active' : roundStatus === 'abandoned' ? 'abandoned' : isExpired ? 'expired' : 'active'}`}>
                                                  {roundStatus === 'completed' ? 'Completed' : roundStatus === 'inuse' ? 'In Progress' : roundStatus === 'abandoned' ? 'Abandoned' : isExpired ? 'Expired' : 'Active'}
                                                </span>
                                              </div>
                                            </div>
                                            <div className="activity-item-body">
                                              <div className="activity-round-schedule">
                                                {scheduledDate && <span className="activity-round-date">Scheduled: {scheduledDate}</span>}
                                                {round.config && (
                                                  <span className="activity-round-config">
                                                    <>
                                                      {round.type === 'coding' && round.config.language && (
                                                        <span className="config-tag">
                                                          {formatCodingLanguageLabel(round.config.language)}
                                                        </span>
                                                      )}
                                                      {round.type === 'aptitude' && round.config.mode && <span className="config-tag">{String(round.config.mode)}</span>}
                                                      {round.config.difficulty && <span className="config-tag">{String(round.config.difficulty)}</span>}
                                                    </>
                                                  </span>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      })
                                    ) : (
                                      <div className="activity-item">
                                        <div className="activity-item-header">
                                          <div className="activity-item-title">{ci.name}</div>
                                          <div className="activity-item-meta">
                                            <span className={`activity-status-badge ${isExpired ? 'expired' : 'active'}`}>
                                              {isExpired ? 'Expired' : 'Active'}
                                            </span>
                                          </div>
                                        </div>
                                        <div className="activity-item-body">
                                          <span className="activity-round-interview-name">No rounds configured</span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      });
                    })()}

                    {!upcomingLoading && upcomingInterviews.length > 0 && filteredUpcomingInterviews.length === 0 && (
                      <AnalyticsStyleEmptyState
                        title={`No ${upcomingStatusFilter === 'all' ? '' : upcomingStatusFilter + ' '}upcoming interviews.`}
                        description={
                          upcomingStatusFilter === 'active'
                            ? 'Try All, Completed, or Expired to see other interviews.'
                            : 'Try a different status filter or reset to Active.'
                        }
                        icon={
                          <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        }
                        onPrimary={upcomingStatusFilter !== 'active' ? () => setUpcomingStatusFilter('active') : handleStartInterview}
                        secondaryLabel="Build Interview"
                        onSecondary={() => navigate('/build-interview')}
                        hints={[
                          { icon: '✓', text: 'Completed — all rounds finished' },
                          { icon: '⏱', text: 'Expired — past the deadline' },
                          { icon: '●', text: 'Active — still open to take' },
                        ]}
                      />
                    )}

                    {!upcomingLoading && upcomingInterviews.length === 0 && (
                      <AnalyticsStyleEmptyState
                        title="No upcoming interviews."
                        description="Check back later for new assignments."
                        icon={
                          <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        }
                        onPrimary={handleStartInterview}
                        onSecondary={() => navigate('/candidate')}
                        hints={[
                          { icon: '📅', text: 'New assignments appear here when your organization shares them' },
                          { icon: '💬', text: 'Practice anytime with Start an Interview' },
                        ]}
                      />
                    )}
                  </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

      </div> {/* Close dashboard-wrapper */}
      </div> {/* Close main content wrapper */}

      {/* Feedback Overlay */}
      {showFeedbackOverlay && (
        <div className="feedback-overlay-backdrop" onClick={closeFeedbackOverlay}>
          <div className="feedback-overlay-container" onClick={(e) => e.stopPropagation()}>
            {overlayLoading && (
              <div className="feedback-overlay-loading">
                <div className="loading-spinner" />
                <span>Loading feedback...</span>
              </div>
            )}

            {!overlayLoading && overlayError && (
              <div className="feedback-overlay-error">
                <span>{overlayError}</span>
                <button onClick={closeFeedbackOverlay}>Close</button>
              </div>
            )}

            {!overlayLoading && !overlayError && overlaySessionType === 'GD' && overlayGdFeedbackData && (
              <GdFeedbackDashboard
                data={overlayGdFeedbackData}
                onClose={closeFeedbackOverlay}
              />
            )}

            {!overlayLoading && !overlayError && overlaySessionType === 'Coding' && overlayCodingFeedbackData && (
              <CodingFeedbackDashboard
                codingFeedback={overlayCodingFeedbackData}
                candidateName={candidateData?.name || 'Candidate'}
                onClose={closeFeedbackOverlay}
              />
            )}

            {!overlayLoading && !overlayError && overlaySessionType !== 'Coding' && overlaySessionType !== 'GD' && overlayFeedbackData && (
              <FeedbackDashboard
                feedbackData={overlayFeedbackData}
                candidateName={candidateData?.name || 'Candidate'}
                interviewMessages={overlayMessages}
                onClose={closeFeedbackOverlay}
              />
            )}
          </div>
        </div>
      )}

      {/* Interview Info Modal */}
      <InterviewDetailsModal
        interviews={infoModalInterviews}
        onClose={() => setInfoModalInterviews(null)}
      />

      {attendRoundTarget && (
        <AttendRoundModal
          roundNum={attendRoundTarget.roundNum}
          roundType={attendRoundTarget.type}
          loading={attendLoading}
          error={attendError}
          loadingNotice={MOCK_INTERVIEW_PUBLISHED_NOTICE}
          showLanguagePicker={attendShowLanguagePicker}
          selectedLanguage={attendSelectedLanguage}
          onLanguageChange={setAttendSelectedLanguage}
          onClose={() => {
            setAttendRoundTarget(null);
            setAttendError('');
          }}
          onStart={handleAttendRound}
        />
      )}
    </div>
    )}
    </>
  );
};

export default CandidateProfile;
